/**
 * envpulse AI — tile do dashboard mostrando saúde geral do tenant.
 *
 * Lado esquerdo: gauge com avg_score + distribuição por grade.
 * Lado direito: top 3 ativos que precisam atenção (com link).
 *
 * Esconde se tenantId for undefined (super_admin sem filtro). Não renderiza
 * empty state — o dashboard já tem TenantStatsCard cobrindo esse caso.
 */

import { Link } from "react-router-dom";
import { AlertCircle, AlertTriangle, ArrowRight, Info, Sparkles } from "lucide-react";
import clsx from "clsx";

import { useTenantInsightsSummary } from "@/hooks/queries";
import { timeAgo } from "@/lib/format";
import type { HealthGrade, InsightSeverity } from "@/types";

import { Card, CardBody, CardHeader } from "./Card";

const GRADE_COLOR: Record<HealthGrade, string> = {
  excellent: "bg-emerald-500",
  good:      "bg-cyan-500",
  fair:      "bg-amber-500",
  poor:      "bg-red-500",
};

const GRADE_LABEL: Record<HealthGrade, string> = {
  excellent: "Excelentes",
  good:      "Boas",
  fair:      "Atenção",
  poor:      "Críticas",
};

const SEV_ICON: Record<InsightSeverity, typeof Info> = {
  info:     Info,
  warning:  AlertTriangle,
  critical: AlertCircle,
};
const SEV_COLOR: Record<InsightSeverity, string> = {
  info:     "text-cyan-500",
  warning:  "text-amber-500",
  critical: "text-red-500",
};


export function TenantAIHealthCard({ tenantId }: { tenantId: string | undefined }) {
  const query = useTenantInsightsSummary(tenantId);

  // Super_admin sem filtro de tenant → não renderiza
  if (!tenantId) return null;

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-violet-500 to-cyan-500 text-white shadow-sm">
              <Sparkles className="h-3.5 w-3.5" />
            </span>
            <span>Saúde do tenant <span className="font-semibold text-violet-600 dark:text-violet-400">· AI</span></span>
          </span>
        }
        subtitle={
          query.data?.last_computed_at
            ? <span className="text-[11px] text-slate-500 dark:text-slate-400">
                Atualizado {timeAgo(query.data.last_computed_at)}
              </span>
            : "Carregando…"
        }
      />
      <CardBody>
        {query.isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="h-32 rounded-lg bg-surface-100 dark:bg-slate-800/40 animate-pulse" />
            <div className="h-32 rounded-lg bg-surface-100 dark:bg-slate-800/40 animate-pulse" />
          </div>
        ) : !query.data || query.data.equipments_total === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Aguardando próximo ciclo do envpulse AI…
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[260px,1fr]">
            <Gauge data={query.data} />
            <NeedsAttention items={query.data.needs_attention} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}


// ----------------------------------------------------------------------------
// Gauge / distribuição
// ----------------------------------------------------------------------------

function Gauge({ data }: { data: NonNullable<ReturnType<typeof useTenantInsightsSummary>["data"]> }) {
  const score = data.avg_score ?? 0;
  // Cor do score grande baseado no avg
  const tone =
    score >= 85 ? "text-emerald-600 dark:text-emerald-400"
    : score >= 70 ? "text-cyan-600 dark:text-cyan-400"
    : score >= 50 ? "text-amber-600 dark:text-amber-400"
    : "text-red-600 dark:text-red-400";

  const total = data.equipments_total;
  const grades: HealthGrade[] = ["excellent", "good", "fair", "poor"];

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className={clsx("text-4xl font-bold tabular-nums", tone)}>
          {score.toFixed(0)}
        </span>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          / 100 · média de {total} ativo{total === 1 ? "" : "s"}
        </span>
      </div>

      {/* Stacked bar de distribuição */}
      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-surface-200 dark:bg-slate-800 flex">
        {grades.map((g) => {
          const count = data.by_grade[g] ?? 0;
          if (count === 0) return null;
          const pct = (count / total) * 100;
          return (
            <div
              key={g}
              className={clsx("h-full", GRADE_COLOR[g])}
              style={{ width: `${pct}%` }}
              title={`${GRADE_LABEL[g]}: ${count}`}
            />
          );
        })}
      </div>

      {/* Legenda */}
      <div className="mt-3 grid grid-cols-2 gap-1.5 text-xs">
        {grades.map((g) => {
          const count = data.by_grade[g] ?? 0;
          return (
            <div key={g} className="flex items-center gap-1.5">
              <span className={clsx("h-2 w-2 rounded-full", GRADE_COLOR[g])} />
              <span className="text-slate-600 dark:text-slate-400">{GRADE_LABEL[g]}</span>
              <span className="ml-auto tabular-nums text-slate-700 dark:text-slate-300">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// Top 3 críticos
// ----------------------------------------------------------------------------

function NeedsAttention({
  items,
}: {
  items: NonNullable<ReturnType<typeof useTenantInsightsSummary>["data"]>["needs_attention"];
}) {
  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        Tudo nos eixos — nenhum ativo precisa atenção agora.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Precisam atenção
      </div>
      <div className="space-y-1.5">
        {items.map((item) => {
          const f = item.top_finding;
          const Icon = f ? SEV_ICON[f.severity] : Info;
          const iconClr = f ? SEV_COLOR[f.severity] : "text-slate-400 dark:text-slate-500";
          return (
            <Link
              key={item.equipment_id}
              to={`/equipments/${item.equipment_id}`}
              className="group flex items-start gap-2 rounded-md border border-surface-200 dark:border-slate-700 bg-surface-50/50 dark:bg-slate-800/40 px-2.5 py-2 hover:bg-surface-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Icon className={clsx("mt-0.5 h-4 w-4 flex-none", iconClr)} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                    {item.equipment_name}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs tabular-nums text-slate-500 dark:text-slate-400">
                    <span className={clsx(
                      "h-1.5 w-1.5 rounded-full",
                      item.health_score >= 70 ? "bg-cyan-500"
                      : item.health_score >= 50 ? "bg-amber-500"
                      : "bg-red-500",
                    )} />
                    {item.health_score}
                  </span>
                </div>
                {item.site_name && (
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    {item.site_name}
                  </div>
                )}
                {f && (
                  <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400 truncate">
                    {f.title}
                  </div>
                )}
              </div>
              <ArrowRight className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
